import React from "react";
import trim from "lodash/trim";
import DayPicker from "../DayPicker";
import PostcodeField from "../Postcode/PostcodeField";
import { FormFieldState } from "../../models/NewExistingOwner";

export enum FieldTypes {
  Text = "Text",
  Static = "Static",
  Email = "Email",
  Phone = "Phone",
  Checkbox = "Checkbox",
  CheckboxBtn = "CheckboxBtn",
  Dob = "Dob",
  Datepicker = "Datepicker",
  Postcode = "Postcode",
}

interface IState extends FormFieldState {}

interface IProps {
  type: FieldTypes;
  label: string;
  optional: boolean;
  defaultValue: any;
  onChange?: Function;
  placeholder?: string;
  minDate?: string | Date;
  maxDate?: string | Date;
  trigger?: any;
  errorMsg?: string;
  className?: string;
  labelDisclaimer?: Function;
}

class FormField extends React.Component<IProps, IState> {
  state: IState = {
    value:
      typeof this.props.defaultValue !== "undefined"
        ? this.props.defaultValue
        : "",
    isValid: true,
  };

  get errorMessage(): string {
    const { label, errorMsg } = this.props;
    const errorMessage = errorMsg || "";

    if (errorMessage.length < 1) {
      return `${label} is required`;
    } else {
      return errorMessage;
    }
  }

  componentWillReceiveProps(props: IProps) {
    if (props.trigger !== this.props.trigger) {
      this.setState({ isValid: this.isValid() }, () =>
        props.onChange(this.state)
      );
    }
  }

  public isValid(value: any = this.state.value): boolean {
    const { type, optional } = this.props;
    let isValid: boolean = true;

    switch (type) {
      case FieldTypes.Text:
      case FieldTypes.Static:
        if (!value || value.length < 1) {
          isValid = false;
        }
        break;

      case FieldTypes.Email:
        if (
          !RegExp(
            /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/
          ).test(value)
        ) {
          isValid = false;
        }
        break;

      case FieldTypes.Phone:
        if (!RegExp(/^\d{10}$/).test(value)) {
          isValid = false;
        }
        break;

      case FieldTypes.Dob:
      case FieldTypes.Datepicker:
        if (
          !RegExp(
            /^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$/
          ).test(value)
        ) {
          isValid = false;
        }
        break;

      case FieldTypes.Postcode:
        if (value === "") {
          isValid = false;
        }
        break;

      case FieldTypes.Checkbox:
        if (value === false && !optional) {
          isValid = false;
        }
        break;
    }

    if (!isValid && optional && value.length < 1) {
      isValid = true;
    }

    return isValid;
  }

  public setValue(value: any): void {
    this.setState({ value, isValid: this.isValid(value) }, () =>
      this.props.onChange(this.state)
    );
  }

  private label(hideOptionalText: boolean = false) {
    const { label, optional, labelDisclaimer } = this.props;
    let labelText: string = label;
    if (optional && !hideOptionalText) {
      labelText = labelText + " (optional)";
    }

    return (
      <label
        className="fieldLabel"
        onClick={() => labelDisclaimer && labelDisclaimer()}
      >
        <span dangerouslySetInnerHTML={{ __html: labelText }} />
        {labelDisclaimer && <span className="fieldLabel__info" />}
      </label>
    );
  }

  private textbox() {
    const { type, onChange, placeholder } = this.props;
    const { value, isValid } = this.state;
    const readOnly = type === FieldTypes.Static;

    return (
      <div className={`formField`}>
        {this.label()}
        <input
          className={isValid ? "text" : "text input-validation-error"}
          type="text"
          value={value}
          readOnly={readOnly}
          disabled={readOnly}
          placeholder={placeholder}
          onChange={(e) => {
            let value = e.target.value;
            const trimmed = trim(e.target.value);
            if (!trimmed.length) {
              value = trimmed;
            }

            if (this.props.type === FieldTypes.Email) {
              value = value.toLowerCase();
            }
            this.setState({ value, isValid: this.isValid(value) }, () =>
              onChange(this.state)
            );
          }}
        />
        {!isValid && (
          <span className="field-validation-error">{this.errorMessage}</span>
        )}
      </div>
    );
  }

  private checkbox() {
    const { onChange } = this.props;
    const { isValid } = this.state;

    return (
      <div
        className="formField checkbox"
        onClick={() => {
          const value = !this.state.value;
          const isValid = this.isValid(value);
          this.setState({ value, isValid }, () => onChange(this.state));
        }}
      >
        {this.label(true)}
        <input
          className={!isValid && "input-validation-error"}
          type="checkbox"
          checked={this.state.value}
          onChange={() => {}}
        />
        {!isValid && (
          <span className="field-validation-error">{this.errorMessage}</span>
        )}
      </div>
    );
  }

  private checkboxBtn() {
    const { onChange } = this.props;

    return (
      <div
        className="formField checkbox checkboxButton"
        onClick={() => {
          const value = !this.state.value;
          const isValid = this.isValid(value);
          this.setState({ value, isValid }, () => onChange(this.state));
        }}
      >
        {this.label(true)}
        <input type="checkbox" checked={this.state.value} onChange={() => {}} />
      </div>
    );
  }

  private dayPicker() {
    const { onChange, minDate, maxDate } = this.props;
    const { isValid } = this.state;

    return (
      <div className="formField datepicker">
        {this.label()}
        <div
          className={`datepicker__wrap ${!isValid && "field-validation-error"}`}
        >
          <DayPicker
            minDate={minDate || null}
            maxDate={maxDate || null}
            onChangeDate={(date) => {
              const value = date.formatted;
              const isValid = this.isValid(value);
              this.setState({ value, isValid }, () => onChange(this.state));
            }}
          />
        </div>
        {!isValid && (
          <span className="field-validation-error">{this.errorMessage}</span>
        )}
      </div>
    );
  }

  private postcode() {
    const { onChange } = this.props;
    const { isValid } = this.state;

    return (
      <div className="formField">
        {this.label()}
        <div style={{ margin: "5px 0" }}>
          <PostcodeField
            doNotRestore={true}
            inputShowsSelected={true}
            saveOnPreSelect={true}
            onSelected={(suburb) => {
              const value = suburb;
              const isValid = this.isValid(value);
              this.setState({ value, isValid }, () => onChange(this.state));
            }}
            fieldClassName={`pc__input--inline ${
              !isValid ? "input-validation-error" : ""
            }`}
            simpleDisplay={true}
          />
        </div>

        {!isValid && (
          <span className="field-validation-error" style={{ display: "block" }}>
            {this.errorMessage}
          </span>
        )}
      </div>
    );
  }

  render() {
    const { className } = this.props;
    const wrapClass: string =
      className && className.length > 0 ? className : "col-xs-12 col-sm-6";
    let field: any = null;

    switch (this.props.type) {
      case FieldTypes.Checkbox:
        field = this.checkbox();
        break;

      case FieldTypes.CheckboxBtn:
        field = this.checkboxBtn();
        break;

      case FieldTypes.Dob:
      case FieldTypes.Datepicker:
        field = this.dayPicker();
        break;

      case FieldTypes.Postcode:
        field = this.postcode();
        break;

      default:
        field = this.textbox();
        break;
    }

    return <div className={wrapClass}>{field}</div>;
  }
}

export default FormField;
